import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';

import { api } from '../lib/api';
import { useAction } from '../lib/hooks';
import { useBilling, useSession } from '../lib/session';
import type { Region, Team } from '../lib/types';
import { Dialog } from './Dialog';
import { ErrorCallout, Spinner } from './ui';

const REGIONS: { value: Region; label: string }[] = [{ value: 'us', label: 'United States' }];

/**
 * POST /v1/teams, then switch to the new organization and open its Members
 * page: making one is almost always about inviting someone.
 */
export function CreateOrganization({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { switchTeam } = useSession();
  const { reloadBilling } = useBilling();
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [region, setRegion] = useState<Region>('us');
  const create = useAction(async () => {
    const team = await api<Team>('/v1/teams', { method: 'POST', body: { name: name.trim(), region } });
    await switchTeam(team.id);
    return team;
  });

  function close() {
    if (create.busy) return;
    setName('');
    create.setError(undefined);
    onClose();
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;
    const team = await create.run();
    if (!team) return;
    void reloadBilling();
    setName('');
    onClose();
    navigate('/members');
  }

  return (
    <Dialog
      open={open}
      onClose={close}
      title="Create organization"
      description="Organizations share keys, sandboxes and one bill with the people you invite."
    >
      <form className="stack" onSubmit={submit} id="create-org">
        <div className="field">
          <label htmlFor="org-name">Name</label>
          <input id="org-name" className="input" autoFocus maxLength={80} placeholder="Acme" value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <div className="field">
          <label htmlFor="org-region">Region</label>
          <select id="org-region" className="select" value={region} onChange={(e) => setRegion(e.target.value as Region)}>
            {REGIONS.map((r) => (
              <option key={r.value} value={r.value}>
                {r.label}
              </option>
            ))}
          </select>
          <p className="hint">Where its Macs run. More regions are coming.</p>
        </div>
        {create.error ? <ErrorCallout error={create.error} /> : null}
        <div className="dialog__actions">
          <button type="button" className="btn" onClick={close} disabled={create.busy}>
            Cancel
          </button>
          <button type="submit" className="btn btn--primary" disabled={!name.trim() || create.busy} aria-busy={create.busy}>
            {create.busy ? <Spinner /> : null}
            Create
          </button>
        </div>
      </form>
    </Dialog>
  );
}
